import React, { createContext, useContext, useEffect, useCallback, useState, useMemo } from 'react';
import { MessageTemplateService, MessageTemplate } from '../services/MessageTemplateService';

interface MessageTemplateContextValue {
  fragments: MessageTemplate[];
  loading: boolean;
  error?: string;
  refresh: () => Promise<void>;
  getFragment: (nameOrId: string) => MessageTemplate | undefined;
}

const MessageTemplateContext = createContext<MessageTemplateContextValue | undefined>(undefined);

export const MessageTemplateProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [fragments, setFragments] = useState<MessageTemplate[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const load = useCallback(async () => {
    setLoading(true);
    setError(undefined);
    try {
      const all = await MessageTemplateService.fetchMessageTemplates();
      setFragments(all);
    } catch (e: any) {
      setError(e?.message || 'Failed to load message fragments');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const getFragment = useCallback((nameOrId: string) => {
    return fragments.find(f => f.id === nameOrId);
  }, [fragments]);

  const value: MessageTemplateContextValue = useMemo(() => ({
    fragments,
    loading,
    error,
    refresh: load,
    getFragment,
  }), [fragments, loading, error, load, getFragment]);

  return (
    <MessageTemplateContext.Provider value={value}>
      {children}
    </MessageTemplateContext.Provider>
  );
};

export const useMessageTemplates = () => {
  const ctx = useContext(MessageTemplateContext);
  if (!ctx) throw new Error('useMessageTemplates must be used within a MessageTemplateProvider');
  return ctx;
};

// Convenience hook for directly obtaining a fragment by name/id.
export const useFragment = (nameOrId: string) => {
  const { getFragment } = useMessageTemplates();
  return getFragment(nameOrId);
};
